import { ChipInput } from "@/shared/components/ui/Input/chipInput";
import CommonInput from "@/shared/components/ui/Input/CommonInput";
import { Link as LinkIcon } from "lucide-react";
import React from "react";
import linkedinIcon from "@/assets/svg/Linkedin-square.svg";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import {
  countryOptions,
  targetIndustriesOptions,
} from "@/shared/utils/onboardingConfig";
import CommonSelect from "@/shared/components/ui/Select/CommonSelect";
import { COMPANY_SIZE_OPTIONS } from "@/shared/utils/contactConfig";

const FormSectionHeading = ({ title, description }) => (
  <div className="border-b border-gray-100 pb-2 dark:border-gray-800">
    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{title}</h3>
    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
  </div>
);

const AddContactForm = ({
  handleSubmit,
  onSubmit,
  onInvalid,
  register,
  errors,
  isSubmitting = false,
  onCancel,
  setValue,
  watch,
  submitText = "Add Contact",
  variant = "default",
}) => {
  const isDetailVariant = variant === "detail";

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onInvalid)}
      className={isDetailVariant
        ? "w-full flex flex-col gap-5 [&_label]:!text-xs [&_input]:!text-sm [&_input]:!font-medium [&_textarea]:!text-sm [&_textarea]:!font-medium"
        : "w-full flex flex-col gap-3"}
    >
      <div className={isDetailVariant
        ? "flex flex-col gap-5 overflow-visible px-0 [&_input]:!min-h-[42px]"
        : "flex flex-col gap-3 overflow-y-auto max-h-[min(450px,calc(90dvh-180px))] px-1"}
      >
        {isDetailVariant ? (
          <FormSectionHeading
            title="Contact details"
            description="Keep the primary contact and communication information up to date."
          />
        ) : null}
        <div className="flex flex-col gap-1.5">
          <label className="text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300">
            Full Name{" "}
            <span className="text-gray-600 dark:text-gray-400 font-semibold ml-0.5">*</span>
          </label>
          <CommonInput
            placeholder="Enter Full Name"
            type="text"
            {...register("fullName")}
            inputClassName="placeholder:!text-[0.6375rem] text-[0.6375rem] text-gray-800 dark:text-gray-100 font-semibold"
          />
          {errors.fullName && (
            <p className="text-[0.6375rem] text-error-500 mt-0.5">
              {errors.fullName.message}
            </p>
          )}
        </div>
        <div className="flex gap-3 w-full">
          <div className="flex flex-col gap-1.5 w-1/2">
            <label className="text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300">
              Designation{" "}
              <span className="text-gray-600 dark:text-gray-400 font-semibold ml-0.5">*</span>
            </label>
            <CommonInput
              placeholder="Enter Designation"
              type="text"
              {...register("designation")}
              name="designation"
              inputClassName="min-w-0 placeholder:!text-[0.6375rem] text-[0.6375rem] text-gray-800 dark:text-gray-100 font-semibold"
            />
            {errors.designation && (
              <p className="text-[0.6375rem] text-error-500 mt-0.5">
                {errors.designation.message}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1.5 w-1/2">
            <label className="text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300">
              Company{" "}
              <span className="text-gray-600 dark:text-gray-400 font-semibold ml-0.5">*</span>
            </label>
            <CommonInput
              placeholder="Enter Company"
              type="text"
              {...register("company")}
              inputClassName="min-w-0 placeholder:!text-[0.6375rem] text-[0.6375rem] text-gray-800 dark:text-gray-100 font-semibold"
            />
            {errors.company && (
              <p className="text-[0.6375rem] text-error-500 mt-0.5">
                {errors.company.message}
              </p>
            )}
          </div>
        </div>
        {isDetailVariant ? (
          <FormSectionHeading
            title="Company information"
            description="Capture the company profile and professional context."
          />
        ) : null}
        <div className="flex gap-3 w-full">
          <div className="flex flex-col gap-1.5 w-1/2">
            <label className="text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300">
              Company LinkedIn
            </label>
            <CommonInput
              placeholder="Company LinkedIn URL"
              type="text"
              {...register("companyLinkedinUrl")}
              inputClassName="min-w-0 placeholder:!text-[0.6375rem] text-[0.6375rem] text-gray-800 dark:text-gray-100 font-semibold"
            />
            {errors.companyLinkedinUrl && (
              <p className="text-[0.6375rem] text-error-500 mt-0.5">
                {errors.companyLinkedinUrl.message}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1.5 w-1/2">
            <label className="text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300">
              Company Website
            </label>
            <CommonInput
              placeholder="Company website"
              type="text"
              {...register("companyWebsite")}
              inputClassName="min-w-0 placeholder:!text-[0.6375rem] text-[0.6375rem] text-gray-800 dark:text-gray-100 font-semibold"
            />
            {errors.companyWebsite && (
              <p className="text-[0.6375rem] text-error-500 mt-0.5">
                {errors.companyWebsite.message}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-3 w-full">
          <div className="flex flex-col gap-1.5 w-1/2">
            <label className="text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300">
              Linkedin URL{" "}
              <span className="text-gray-400 dark:text-gray-500 font-normal ml-0.5">(Optional)</span>
            </label>
            <CommonInput
              placeholder="Enter Linkedin URL"
              type="text"
              {...register("linkedinUrl")}
              rightIcon={<LinkIcon size={16} className="text-gray-500 dark:text-gray-400" />}
              icon={
                <img src={linkedinIcon} alt="Linkedin" className="w-4 h-4" />
              }
              inputClassName="placeholder:!text-[0.6375rem] text-[0.6375rem] text-gray-800 dark:text-gray-100 font-semibold"
            />
            {errors.linkedinUrl && (
              <p className="text-[0.6375rem] text-error-500 mt-0.5">
                {errors.linkedinUrl.message}
              </p>
            )}
          </div>
          <div className="flex min-w-0 w-1/2 flex-col gap-1.5">
            <label className="text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300">
              Company Size{" "}
              <span className="text-gray-600 dark:text-gray-400 font-semibold ml-0.5">*</span>
            </label>
            <CommonSelect
              options={COMPANY_SIZE_OPTIONS}
              placeholder="Select Company Size"
              width="w-full"
              value={COMPANY_SIZE_OPTIONS.find(
                (opt) => opt.value === watch("companySize"),
              )}
              onChange={(selectedKey) => {
                const next =
                  typeof selectedKey === "string" ||
                  typeof selectedKey === "number"
                    ? String(selectedKey)
                    : "";
                setValue("companySize", next, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              classNames={{
                mainWrapper:
                  `${isDetailVariant ? "!text-sm" : "!text-[0.6375rem]"} w-full min-w-0 max-w-full shrink`,
                value: `${isDetailVariant ? "!text-sm" : "!text-[0.6375rem]"} !text-gray-800 dark:!text-gray-100 !font-semibold`,
                base: `bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 shadow-sm ${isDetailVariant ? "!text-sm h-[42px]" : "!text-[0.6375rem] h-[32.6px]"} w-full max-w-full min-w-0 hover:bg-white dark:hover:bg-gray-800`,
                trigger:
                  `bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 shadow-sm ${isDetailVariant ? "!text-sm h-[42px]" : "!text-[0.6375rem] h-[32.6px]"} w-full max-w-full min-w-0 hover:bg-white dark:hover:bg-gray-800`,
              }}
            />
            {errors.companySize && (
              <p className="text-[0.6375rem] text-error-500 mt-0.5">
                {errors.companySize.message}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-3 w-full">
          <div className="flex flex-col gap-1.5 w-1/2">
            <label className="text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300">
              Email <span className="text-gray-600 dark:text-gray-400 font-semibold ml-0.5">*</span>
            </label>
            <CommonInput
              placeholder="Enter Email"
              type="email"
              {...register("email")}
              inputClassName="placeholder:!text-[0.6375rem] text-[0.6375rem] text-gray-800 dark:text-gray-100 font-semibold"
            />
            {errors.email && (
              <p className="text-[0.6375rem] text-error-500 mt-0.5">
                {errors.email.message}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1.5 w-1/2">
            <label className="text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1">
              Whatsapp Number
            </label>
            <CommonInput
              placeholder="Enter Whatsapp Number"
              type="text"
              autoComplete="new-password"
              {...register("whatsappNumber")}
              inputClassName="placeholder:!text-[0.6375rem] text-[0.6375rem] text-gray-800 dark:text-gray-100 font-semibold"
            />
            {errors.whatsappNumber && (
              <p className="text-[0.6375rem] text-error-500 mt-0.5">
                {errors.whatsappNumber.message}
              </p>
            )}
          </div>
        </div>
        {isDetailVariant ? (
          <FormSectionHeading
            title="CRM details"
            description="Use these fields to qualify and segment the contact."
          />
        ) : null}
        <div className="flex gap-3 w-full">
          <div className="flex flex-col gap-1.5 w-1/2">
            <label className="text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300">
              Lead Source
            </label>
            <CommonInput
              placeholder="Lead source"
              type="text"
              {...register("leadSource")}
              inputClassName="min-w-0 placeholder:!text-[0.6375rem] text-[0.6375rem] text-gray-800 dark:text-gray-100 font-semibold"
            />
          </div>
          <div className="flex flex-col gap-1.5 w-1/2">
            <label className="text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300">
              Social Lead ID
            </label>
            <CommonInput
              placeholder="Social lead ID"
              type="text"
              {...register("socialLeadId")}
              inputClassName="min-w-0 placeholder:!text-[0.6375rem] text-[0.6375rem] text-gray-800 dark:text-gray-100 font-semibold"
            />
          </div>
        </div>
        <div className="flex gap-3 w-full">
          <div className="flex flex-col gap-1.5 w-1/2">
            <label className="text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300">
              Buying Role
            </label>
            <CommonInput
              placeholder="Buying role"
              type="text"
              {...register("buyingRole")}
              inputClassName="min-w-0 placeholder:!text-[0.6375rem] text-[0.6375rem] text-gray-800 dark:text-gray-100 font-semibold"
            />
          </div>
          <div className="flex flex-col gap-1.5 w-1/2">
            <label className="text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300">
              Persona
            </label>
            <CommonInput
              placeholder="Persona"
              type="text"
              {...register("persona")}
              inputClassName="min-w-0 placeholder:!text-[0.6375rem] text-[0.6375rem] text-gray-800 dark:text-gray-100 font-semibold"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300">
            Description
          </label>
          <textarea
            {...register("description")}
            rows={3}
            placeholder="Add a short description"
            className="w-full resize-y rounded-xl border border-gray-300 bg-white px-3 py-2 text-[0.6375rem] font-semibold text-gray-800 outline-none placeholder:text-gray-400 focus:border-secondary dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          />
          {errors.description && (
            <p className="text-[0.6375rem] text-error-500 mt-0.5">
              {errors.description.message}
            </p>
          )}
        </div>
        <label className="flex items-center gap-2 text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300">
          <input
            type="checkbox"
            {...register("emailOptOut")}
            className="h-3.5 w-3.5 rounded border-gray-300 text-secondary focus:ring-secondary"
          />
          Email Opt Out
        </label>
        {isDetailVariant ? (
          <FormSectionHeading
            title="Segmentation"
            description="Add the contact's market and location information."
          />
        ) : null}
        {/* industry */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300">
            Industry{" "}
            <span className="text-gray-600 dark:text-gray-400 font-semibold ml-0.5">*</span>
          </label>
          <ChipInput
            defaultChips={watch("industry") ?? []}
            placeholder="Select from suggestion or type new & press enter to add it!"
            dropdownOptions={targetIndustriesOptions}
            onChange={(industry) => {
              setValue("industry", industry, {
                shouldValidate: true,
              });
            }}
            errors={errors.industry}
            className="bg-white dark:bg-gray-800"
            classNameChips={`${isDetailVariant ? "!text-sm" : "!text-[0.6375rem]"} !bg-gray-50 dark:!bg-gray-800 !font-semibold !text-gray-800 dark:!text-gray-100 !py-1 !px-2.5 h-auto`}
          />
          {errors.industry && (
            <p className="text-[0.6375rem] text-error-500 mt-0.5">
              {errors.industry.message}
            </p>
          )}
        </div>

        {/* country */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[0.6375rem] font-medium text-gray-600 dark:text-gray-300">
            Country <span className="text-gray-600 dark:text-gray-400 font-semibold ml-0.5">*</span>
          </label>
          <ChipInput
            defaultChips={watch("country") ?? []}
            placeholder="Select from suggestion or type new & press enter to add it!"
            dropdownOptions={countryOptions}
            onChange={(country) => {
              setValue("country", country, {
                shouldValidate: true,
              });
            }}
            className="bg-white dark:bg-gray-800"
            classNameChips={`${isDetailVariant ? "!text-sm" : "!text-[0.6375rem]"} !bg-gray-50 dark:!bg-gray-800 !font-semibold !text-gray-800 dark:!text-gray-100 !py-1 !px-2.5 h-auto`}
            errors={errors.country}
          />
          {errors.country && (
            <p className="text-[0.6375rem] text-error-500 mt-0.5">
              {errors.country.message}
            </p>
          )}
        </div>

        {/* <div className="flex flex-col gap-1.5">
          <label className="text-[0.6375rem] font-medium text-gray-600">
            Tags
          </label>
          <ChipInput
            defaultChips={watch("tags") ?? []}
            placeholder="Type a tag & press enter to add"
            dropdownOptions={[]}
            onChange={(nextTags) => {
              setValue("tags", nextTags, {
                shouldValidate: true,
              });
            }}
            errors={errors.tags}
          />
          {errors.tags && (
            <p className="text-[0.6375rem] text-error-500 mt-0.5">
              {errors.tags.message}
            </p>
          )}
        </div> */}
      </div>
      <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-5 dark:border-gray-800">
        <SecondaryButton
          title="Cancel"
          className={isDetailVariant ? "px-5 py-2.5 text-sm shadow-none text-gray-600 font-medium" : "text-xs shadow-none text-gray-600 font-normal"}
          onClick={() => onCancel()}
        />
        <PrimaryButton
          type="submit"
          startContent={false}
          disabled={isSubmitting}
          isLoading={isSubmitting}
          className={isDetailVariant ? "px-5 py-2.5 text-sm font-medium" : "text-xs font-normal"}
        >
          {isSubmitting ? "Saving..." : submitText}
        </PrimaryButton>
      </div>
    </form>
  );
};

export default AddContactForm;
